import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { computeDisplayNames } from "@/lib/displayNames";
import { PageHeader, Chip, EmptyState } from "@/components/ui-bits";
import { Plus, X, Pencil, Trash2, Share2, Search } from "lucide-react";
import { toast } from "sonner";
import EmployeeDetail from "@/components/EmployeeDetail";
import IndividualPermissions from "@/components/IndividualPermissions";
import { SeatQuotaBanner, SeatLimitReachedModal, useSeatUsage, parseSeatLimitError } from "@/components/SeatQuota";
import ConfirmDialog from "@/components/ConfirmDialog";
import HoldToDeleteModal from "@/components/HoldToDeleteModal";
import InviteCodeModal from "@/components/InviteCodeModal";
import TrashModal from "@/components/TrashModal";
import AvatarUpload from "@/components/AvatarUpload";
import Avatar from "@/components/Avatar";
import { toastWithUndo } from "@/lib/undo";

const empty = { email: "", password: "", name: "", role: "employee", phone: "", language: "nl", location_id: "", team_id: "", date_of_birth: "", department: "", job_title: "", managed_location_ids: [] };

// Robust role-label helper. Falls back to a capitalised version of the raw
// role key when a translation is missing (e.g. legacy `hr` role).
function roleLabelFor(role, t) {
  if (!role) return "—";
  const label = t(`role_${role}`);
  if (!label || label === `role_${role}`) {
    return role.charAt(0).toUpperCase() + role.slice(1);
  }
  return label;
}

// Empty / punctuation-only names sometimes exist for demo/test data. Show a
// friendly placeholder instead of a single dot or empty string.
function safeName(u, displayNames, t) {
  const dn = displayNames?.get?.(u._id) || u.name;
  if (!dn || String(dn).replace(/[.\-_\s]/g, "").length === 0) {
    return t("emp_no_name") || "Naamloos";
  }
  return dn;
}

// Small colored hours-vs-contract chip. Feb 2026 — helps managers spot at a
// glance who's under- or over-scheduled this week without opening the widget.
// Colors mirror the dashboard "Hours vs contract" widget.
function HoursBadge({ row, t }) {
  if (!row || !row.contract_hours) return null;
  const c = row.status === "over"
    ? { fg: "hsl(0 75% 40%)",  bg: "hsl(0 75% 55% / 0.14)"  }
    : row.status === "under"
    ? { fg: "hsl(30 90% 42%)", bg: "hsl(38 92% 50% / 0.16)" }
    : { fg: "hsl(160 65% 32%)", bg: "hsl(160 65% 42% / 0.16)" };
  const label = row.status === "over"
    ? t("hvc_badge_over") || "boven contract"
    : row.status === "under"
    ? t("hvc_badge_under") || "onder contract"
    : t("hvc_badge_on") || "op contract";
  return (
    <span
      title={`${label} · ${row.pct.toFixed(0)}% (${t("hours_this_week") || "deze week"})`}
      className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[10.5px] font-bold tabular-nums leading-none"
      style={{ background: c.bg, color: c.fg }}
      data-testid={`emp-hours-${row.user_id}`}
    >
      {row.planned_hours.toFixed(1)}<span className="opacity-60 font-medium">/{row.contract_hours.toFixed(0)}u</span>
    </span>
  );
}

async function generateInvite(role = "employee", t, opts) {
  return generateInviteInner(role, t, opts);
}
const generateInviteInner = async (role, t, { onSeatLimit, onSuccess, onInvite } = {}) => {
  try {
    const { data } = await api.post("/invitations", { role });
    onInvite?.(data); // { code, invitation, join_url }
    onSuccess?.();
    return data;
  } catch (e) {
    const { isSeatLimit, detail } = parseSeatLimitError(e);
    if (isSeatLimit) { onSeatLimit?.(detail); return; }
    toast.error(e.response?.data?.detail || (t ? t("code_error") : "Kon geen code aanmaken"));
  }
};

export default function Employees() {
  const { t } = useI18n();
  const { user: currentUser } = useAuth();
  const isOwner = currentUser?.role === "owner";
  const [list, setList] = useState([]);
  const displayNames = useMemo(() => computeDisplayNames(list), [list]);
  const [locations, setLocations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [filter, setFilter] = useState("");
  // Feb 2026: quick filter pills to slice by role. "all" = geen filter.
  const [roleFilter, setRoleFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const { usage, refresh: refreshUsage } = useSeatUsage();
  const [seatLimit, setSeatLimit] = useState(null); // holds { current, max, message } when hit
  const [confirmDelUser, setConfirmDelUser] = useState(null);
  // Second-stage hold-to-confirm modal. Only opens after the user clicks
  // "Verwijderen" on the first ConfirmDialog. Requires a 10-second sustained
  // press before the actual DELETE call fires — no undo toast afterwards.
  const [holdDelUser, setHoldDelUser] = useState(null);
  const [inviteData, setInviteData] = useState(null); // { code, role, joinUrl }
  const [showTrash, setShowTrash] = useState(false);
  // Map<user_id, {planned_hours, contract_hours, pct, status}> — used to color
  // each employee card/row green/orange/red based on this week's schedule.
  const [hoursByUser, setHoursByUser] = useState(new Map());
  // Fase 3.2 — fetch the current company settings once so the Individual
  // Permissions component can render "Role default: Allow/Deny" text.
  const [companySettings, setCompanySettings] = useState({});

  const load = async () => {
    const [u, l, tm, hc, co] = await Promise.all([
      api.get("/users"),
      api.get("/locations"),
      api.get("/teams"),
      api.get("/dashboard/hours-vs-contract").catch(() => ({ data: { rows: [] } })),
      api.get("/companies/me").catch(() => ({ data: { settings: {} } })),
    ]);
    setList(u.data); setLocations(l.data); setTeams(tm.data);
    setHoursByUser(new Map((hc.data?.rows || []).map((r) => [r.user_id, r])));
    setCompanySettings(co.data?.settings || {});
    refreshUsage();
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Deep-link support: /employees?open=<userId> auto-opens the detail panel.
  // Used from the ⌘K command palette and any other "jump to employee" link.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || list.length === 0) return;
    const target = list.find((u) => u._id === openId);
    if (target) {
      setDetail(target);
      // Strip the query param so refreshing doesn't reopen unexpectedly.
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [list, searchParams, setSearchParams]);

  const openNew = () => {
    // Proactively check quota before opening the new-employee modal
    if (usage && usage.max != null && usage.remaining <= 0) {
      setSeatLimit({
        code: "seat_limit_reached",
        message: t("seat_modal_body_generic"),
        current: usage.current,
        max: usage.max,
      });
      return;
    }
    setEditing(null); setForm(empty); setShowModal(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      ...empty,
      ...u,
      password: "",
      managed_location_ids: Array.isArray(u.managed_location_ids) ? [...u.managed_location_ids] : [],
    });
    setShowModal(true);
  };

  const save = async () => {
    try {
      const isMgmtRole = form.role === "manager" || form.role === "teamlead";
      const shared = {
        name: form.name,
        phone: form.phone,
        language: form.language,
        role: form.role,
        location_id: form.location_id || null,
        team_id: form.team_id || null,
        date_of_birth: form.date_of_birth || null,
        department: form.department || null,
        job_title: form.job_title || null,
      };
      // Only owners can set/change the manager-location scope; skip for non-mgmt roles.
      if (isOwner && isMgmtRole) {
        shared.managed_location_ids = form.managed_location_ids || [];
      }
      if (editing) {
        await api.patch(`/users/${editing._id}`, shared);
      } else {
        await api.post("/users", { email: form.email, password: form.password, ...shared });
      }
      toast.success(t("saved"));
      setShowModal(false); load();
    } catch (e) {
      const { isSeatLimit, detail } = parseSeatLimitError(e);
      if (isSeatLimit) { setShowModal(false); setSeatLimit(detail); return; }
      toast.error(e.response?.data?.detail || t("error"));
    }
  };

  const del = (u) => setConfirmDelUser(u);
  // Step 1 → step 2: hop the user to the hold-to-delete stage. Nothing is
  // deleted yet; we only escalate the confirmation UX.
  const askHoldDelete = () => {
    const u = confirmDelUser;
    setConfirmDelUser(null);
    if (u) setHoldDelUser(u);
  };
  // Step 2 completes (10s hold) — permanent, unrecoverable delete. We first
  // soft-delete (marks `deleted_at`) then immediately hard-delete via the
  // /permanent endpoint so the user is fully purged (no trash entry, no way
  // to undo). Seat usage is refreshed so the count drops right away.
  const doHardDelete = async () => {
    const u = holdDelUser;
    if (!u) return;
    try {
      await api.delete(`/users/${u._id}`);
      try { await api.delete(`/users/${u._id}/permanent`); } catch { /* already gone or trash-only, tolerate */ }
      setList((prev) => prev.filter((x) => x._id !== u._id));
      refreshUsage();
      toast.success(t("employee_deleted_final") || t("employee_deleted") || "Medewerker definitief verwijderd");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    } finally {
      setTimeout(() => setHoldDelUser(null), 600);
    }
  };

  const openInviteFlow = (role = "employee") => {
    generateInvite(role, t, {
      onSeatLimit: setSeatLimit,
      onSuccess: refreshUsage,
      onInvite: (data) => {
        setInviteData({
          code: data.code,
          role,
          joinUrl: data.join_url || `${window.location.origin}/join?code=${data.code}`,
        });
      },
    });
  };

  const filtered = list.filter((u) => {
    const q = filter.toLowerCase();
    const matchesText = !q || u.name?.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      || u.job_title?.toLowerCase().includes(q) || u.department?.toLowerCase().includes(q);
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesText && matchesRole;
  });

  // Small counts used by the role-filter pills.
  const roleCounts = useMemo(() => {
    const c = { all: list.length, employee: 0, teamlead: 0, manager: 0, owner: 0 };
    for (const u of list) if (c[u.role] != null) c[u.role]++;
    return c;
  }, [list]);

  return (
    <div data-testid="page-employees">
      <PageHeader
        eyebrow={t("employees")}
        title={t("employees")}
        subtitle={usage && usage.max != null
          ? `${usage.current} van ${usage.max} medewerker${usage.max === 1 ? "" : "s"} gebruikt`
          : `${filtered.length} van ${list.length} medewerker${list.length === 1 ? "" : "s"}`}
      />

      {/* Desktop action bar — losstaand van de header, in een eigen card zodat
          alle knoppen altijd zichtbaar zijn zonder te hoeven scrollen. Feb 2026. */}
      <div className="hidden md:flex items-center gap-2 card p-2.5 mb-5 flex-wrap" data-testid="emp-action-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
          <input
            data-testid="search-employees-desktop"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t("search_placeholder")}
            className="w-full h-10 pl-9 pr-3 rounded-xl bg-[hsl(var(--bg))] border hairline text-[13.5px] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)_/_0.15)] transition-all"
          />
        </div>
        <button onClick={() => openInviteFlow("employee")} className="btn btn-ghost !h-10 shrink-0" data-testid="invite-employee">
          <Share2 size={14} />{t("invite_code_action")}
        </button>
        <button onClick={() => setShowTrash(true)} className="btn btn-ghost !h-10 shrink-0" data-testid="open-trash" title={t("trash_title")}>
          <Trash2 size={14} />{t("trash_open")}
        </button>
        <button onClick={openNew} className="btn btn-primary !h-10 shrink-0" data-testid="add-employee">
          <Plus size={14} />{t("add_employee")}
        </button>
      </div>

      {/* Mobile action bar — sticky, always tappable */}
      <div className="md:hidden sticky top-2 z-10 -mx-4 px-4 py-3 space-y-2.5 bg-[hsl(var(--bg)_/_0.85)] backdrop-blur-md">
        <input
          data-testid="search-employees"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t("emp_search_placeholder")}
          className="w-full h-12 px-4 rounded-2xl bg-white border hairline text-[15px] outline-none focus:border-[hsl(var(--primary))] focus:ring-2 focus:ring-[hsl(var(--primary)_/_0.15)]"
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => openInviteFlow("employee")}
            className="btn btn-ghost !h-11 !text-[13px]"
            data-testid="invite-employee-mobile"
          >
            <Share2 size={14} strokeWidth={1.9} />{t("invite_short")}
          </button>
          <button onClick={openNew} className="btn btn-primary !h-11 !text-[13px]" data-testid="add-employee-mobile">
            <Plus size={14} strokeWidth={2.2} />{t("add_employee")}
          </button>
        </div>
      </div>

      <SeatQuotaBanner usage={usage} onUpgrade={() => setSeatLimit({ code: "seat_limit_reached", current: usage?.current, max: usage?.max, message: t("seat_modal_body_generic") })} />
      <SeatLimitReachedModal open={!!seatLimit} detail={seatLimit} onClose={() => setSeatLimit(null)} />

      {filtered.length === 0 ? (
        <EmptyState title={t("no_data")} />
      ) : (
        <>
          {/* Mobile: big-touch card list */}
          <div className="md:hidden flex flex-col gap-2.5 mt-2">
            {filtered.map((u) => {
              const loc = locations.find((l) => l._id === u.location_id);
              const tm = teams.find((tt) => tt._id === u.team_id);
              return (
                <motion.div
                  key={u._id}
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="card p-4 flex items-center gap-3 active:scale-[0.99] transition-transform"
                  onClick={() => setDetail(u)}
                  data-testid={`emp-mobile-${u._id}`}
                >
                  <Avatar user={u} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] truncate leading-tight">{safeName(u, displayNames, t)}</div>
                    </div>
                    <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5 truncate">
                      {u.job_title || roleLabelFor(u.role, t)}{loc ? ` · ${loc.name}` : ""}{tm ? ` · ${tm.name}` : ""}
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Chip variant="primary">{roleLabelFor(u.role, t)}</Chip>
                      <HoursBadge row={hoursByUser.get(u._id)} t={t} />
                      {u.date_of_birth && (
                        <span className="text-[11px] text-[hsl(var(--text-muted))] tabular-nums">
                          🎂 {new Date(u.date_of_birth + "T00:00:00").toLocaleDateString("nl", { day: "numeric", month: "short" })}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEdit(u); }}
                    className="w-10 h-10 shrink-0 rounded-xl grid place-items-center text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--muted))] transition-colors"
                    data-testid={`edit-emp-mobile-${u._id}`}
                    aria-label={t("edit")}
                  >
                    <Pencil size={15} strokeWidth={1.9} />
                  </button>
                </motion.div>
              );
            })}
          </div>

          {/* Desktop: cleaner table with avatar column.
              Progressive column reveal: only the essentials (avatar, name,
              role, actions) always show → job function/location/hours reveal
              at lg → department/birthday at xl. Result: on standaard schermen
              geen horizontale scrollbalk meer nodig, namen áltijd zichtbaar. */}
          <div className="hidden md:block card mt-4">
            <table className="w-full text-sm table-fixed">
            <thead className="border-b hairline bg-[hsl(var(--muted)_/_0.4)]">
              <tr className="text-left">
                <th className="p-3 pl-4 eyebrow w-[52px]"></th>
                <th className="p-3 eyebrow">{t("name")}</th>
                <th className="p-3 eyebrow hidden lg:table-cell w-[160px]">{t("job_function")}</th>
                <th className="p-3 eyebrow hidden xl:table-cell w-[140px]">{t("department_col")}</th>
                <th className="p-3 eyebrow w-[130px]">{t("role")}</th>
                <th className="p-3 eyebrow hidden lg:table-cell w-[140px]">{t("location")}</th>
                <th className="p-3 eyebrow hidden xl:table-cell w-[110px]">{t("hours_col") || "Uren"}</th>
                <th className="p-3 eyebrow hidden xl:table-cell w-[110px]">{t("birthday_col")}</th>
                <th className="p-3 pr-4 eyebrow text-right w-[100px]">{t("edit")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr
                  key={u._id}
                  onClick={() => setDetail(u)}
                  className="border-t hairline hover:bg-[hsl(var(--muted))] cursor-pointer transition-colors"
                  data-testid={`emp-${u._id}`}
                >
                  <td className="p-3 pl-4">
                    <Avatar user={u} size={34} />
                  </td>
                  <td className="p-3">
                    <div className="font-semibold text-[hsl(var(--text-strong))] leading-tight truncate">{safeName(u, displayNames, t)}</div>
                    <div className="font-mono text-[11px] text-[hsl(var(--text-muted))] truncate">{u.email}</div>
                  </td>
                  <td className="p-3 text-[13px] hidden lg:table-cell truncate">{u.job_title || <span className="text-[hsl(var(--text-muted))]">—</span>}</td>
                  <td className="p-3 text-[13px] hidden xl:table-cell truncate">{u.department || <span className="text-[hsl(var(--text-muted))]">—</span>}</td>
                  <td className="p-3"><Chip variant="primary">{roleLabelFor(u.role, t)}</Chip></td>
                  <td className="p-3 text-[13px] hidden lg:table-cell truncate">{locations.find((l) => l._id === u.location_id)?.name || <span className="text-[hsl(var(--text-muted))]">—</span>}</td>
                  <td className="p-3 text-[13px] hidden xl:table-cell">
                    {hoursByUser.get(u._id)?.contract_hours
                      ? <HoursBadge row={hoursByUser.get(u._id)} t={t} />
                      : <span className="text-[hsl(var(--text-muted))]">—</span>}
                  </td>
                  <td className="p-3 text-[13px] font-mono hidden xl:table-cell">
                    {u.date_of_birth
                      ? new Date(u.date_of_birth + "T00:00:00").toLocaleDateString("nl", { day: "numeric", month: "short" })
                      : <span className="text-[hsl(var(--text-muted))]">—</span>}
                  </td>
                  <td className="p-3 pr-4 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); openEdit(u); }} className="btn btn-ghost !h-8 !px-2" data-testid={`edit-emp-${u._id}`} title={t("edit")} aria-label={t("edit")}><Pencil size={12} /></button>
                      <button onClick={(e) => { e.stopPropagation(); del(u); }} className="btn btn-ghost !h-8 !px-2 hover:!text-[hsl(var(--danger))]" data-testid={`del-emp-${u._id}`} title={t("delete")} aria-label={t("delete")}><Trash2 size={12} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      {detail && (
        <EmployeeDetail
          employee={detail}
          locations={locations}
          teams={teams}
          onClose={() => setDetail(null)}
          onEdit={() => { openEdit(detail); setDetail(null); }}
        />
      )}

      {showModal && (
        <Modal
          title={editing ? t("edit") : t("add_employee")}
          onClose={() => setShowModal(false)}
        >
          {/* Avatar upload — only visible when editing an existing user so
              we can PATCH /users/{id} directly. Feb 2026. */}
          {editing && (
            <div
              className="flex items-center gap-4 pb-4 mb-5 border-b hairline"
              data-testid="emp-avatar-row"
            >
              <AvatarUpload
                user={editing}
                size={72}
                onChange={(url) => setEditing({ ...editing, avatar_url: url })}
              />
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{t("avatar_label")}</div>
                <div className="text-[11.5px] text-[hsl(var(--text-muted))] leading-snug mt-0.5">
                  {t("avatar_hint")}
                </div>
              </div>
            </div>
          )}

          {/* Section: Personal & contact */}
          <div className="eyebrow text-[10.5px] mb-2 mt-1">{t("emp_section_personal")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <F label={t("name")}><input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></F>
            <F label={t("email")}><input data-testid="emp-email" type="email" disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></F>
            {!editing && (
              <F label={t("password")}><input data-testid="emp-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inp} /></F>
            )}
            <F label={t("phone")}><input data-testid="emp-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} /></F>
            <F label={t("birthday_col")}>
              <input data-testid="emp-dob" type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className={inp} />
            </F>
            <F label={t("language")}>
              <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className={inp}>
              {["nl", "en"].map((l) => (<option key={l} value={l}>{l.toUpperCase()}</option>))}
              </select>
            </F>
          </div>

          {/* Section: Role & assignment */}
          <div className="eyebrow text-[10.5px] mb-2">{t("emp_section_role")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
            <F label={t("role")}>
              <select data-testid="emp-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inp}>
                <option value="employee">{t("role_employee")}</option>
                <option value="teamlead">{t("role_teamlead")}</option>
                <option value="manager">{t("role_manager")}</option>
              </select>
            </F>
            <F label={t("job_function")}>
              <input data-testid="emp-jobtitle" value={form.job_title || ""} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder={t("job_placeholder")} className={inp} />
            </F>
            <F label={t("department_col")}>
              <input data-testid="emp-department" value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder={t("dept_placeholder")} className={inp} />
            </F>
            <F label={t("location")}>
              <select value={form.location_id || ""} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className={inp}>
                <option value="">—</option>
                {locations.map((l) => (<option key={l._id} value={l._id}>{l.name}</option>))}
              </select>
            </F>
            <F label={t("team")}>
              <select value={form.team_id || ""} onChange={(e) => setForm({ ...form, team_id: e.target.value })} className={inp}>
                <option value="">—</option>
                {teams.map((tm) => (<option key={tm._id} value={tm._id}>{tm.name}</option>))}
              </select>
            </F>
          </div>

          {/* Section: Manager location scope — owner-only, for management roles */}
          {isOwner && (form.role === "manager" || form.role === "teamlead") && editing && (
            <IndividualPermissions
              user={editing}
              companySettings={companySettings}
              onSaved={(next) => { if (next) setEditing({ ...editing, ...next }); load(); }}
            />
          )}
          {isOwner && (form.role === "manager" || form.role === "teamlead") && (
            <>
              <div className="eyebrow text-[10.5px] mb-2">{t("emp_section_scope")}</div>
              <div className="mb-2">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] text-[hsl(var(--text-muted))] leading-snug">
                    {t("managed_locations_hint")}
                  </span>
                  <div className="flex flex-wrap gap-2 p-2.5 rounded-xl border hairline bg-[hsl(var(--muted)_/_0.3)] mt-1" data-testid="emp-managed-locations">
                    {locations.length === 0 && (
                      <span className="text-[12px] text-[hsl(var(--text-muted))] px-1">
                        {t("managed_locations_none_yet")}
                      </span>
                    )}
                    {locations.map((l) => {
                      const active = (form.managed_location_ids || []).includes(l._id);
                      return (
                        <button
                          key={l._id}
                          type="button"
                          data-testid={`emp-loc-toggle-${l._id}`}
                          onClick={() => {
                            const cur = new Set(form.managed_location_ids || []);
                            if (cur.has(l._id)) cur.delete(l._id); else cur.add(l._id);
                            setForm({ ...form, managed_location_ids: [...cur] });
                          }}
                          className={`text-[12px] font-medium px-2.5 py-1.5 rounded-full transition-colors border ${
                            active
                              ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]"
                              : "bg-[hsl(var(--surface))] text-[hsl(var(--text))] hairline hover:bg-[hsl(var(--muted))]"
                          }`}
                        >
                          <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: l.color || "#0033A0" }} />
                          {l.name}
                        </button>
                      );
                    })}
                  </div>
                  <span className="text-[11px] text-[hsl(var(--text-muted))] mt-1">
                    {(form.managed_location_ids || []).length === 0
                      ? t("managed_locations_all_selected")
                      : t("managed_locations_x_selected").replace("{n}", (form.managed_location_ids || []).length)}
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Sticky footer action row */}
          <div className="sticky bottom-0 -mx-6 -mb-5 px-6 py-3 border-t hairline bg-[hsl(var(--surface))] flex justify-end gap-2 mt-6 rounded-b-2xl">
            <button onClick={() => setShowModal(false)} className="btn btn-ghost" data-testid="emp-cancel">{t("cancel")}</button>
            <button onClick={save} className="btn btn-primary" data-testid="emp-save">{t("save")}</button>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={!!confirmDelUser}
        title={t("confirm_delete_title")}
        body={confirmDelUser ? t("confirm_delete_employee").replace("{name}", confirmDelUser.name || confirmDelUser.email) : ""}
        confirmLabel={t("delete")}
        onConfirm={askHoldDelete}
        onCancel={() => setConfirmDelUser(null)}
        testid="confirm-employee"
      />

      <HoldToDeleteModal
        open={!!holdDelUser}
        title={t("hold_delete_emp_title") || "Definitief verwijderen"}
        body={t("hold_delete_emp_body") || "Om te voorkomen dat je per ongeluk klikt: houd de rode knop 10 seconden vast. Laat je los, dan begint de teller opnieuw. Na 10 seconden is de medewerker weg — dit kan daarna niet meer ongedaan worden."}
        itemLabel={holdDelUser?.name || holdDelUser?.email || ""}
        onConfirmed={doHardDelete}
        onCancel={() => setHoldDelUser(null)}
        testid="hold-delete-emp"
      />

      <InviteCodeModal
        open={!!inviteData}
        code={inviteData?.code}
        role={inviteData?.role}
        joinUrl={inviteData?.joinUrl}
        onClose={() => setInviteData(null)}
      />

      <TrashModal open={showTrash} onClose={() => setShowTrash(false)} onChange={load} />
    </div>
  );
}

const inp = "input-field !h-10 !text-[13px] !rounded-xl min-w-0";
function F({ label, children }) { return (<label className="flex flex-col gap-1"><span className="eyebrow">{label}</span>{children}</label>); }

export function Modal({ title, children, onClose, size = "lg" }) {
  const { t } = useI18n();
  // Feb 2026: made the modal wider by default and gave it max-h + scrolling
  // for long forms so contents don't get pushed off-screen. Also added a
  // subtle fade-in wrapper (no y-slide) so opening doesn't feel jumpy.
  const widthClass =
    size === "xl" ? "md:max-w-3xl" :
    size === "md" ? "md:max-w-md" :
    "md:max-w-2xl";
  return (
    <div
      className="fixed inset-0 z-40 bg-black/50 grid md:place-items-center items-end justify-items-center p-0 md:p-4 overflow-y-auto animate-[fadeIn_120ms_ease-out]"
      onClick={onClose}
      data-testid="modal-backdrop"
    >
      <div
        className={`bg-[hsl(var(--surface))] border hairline w-full ${widthClass} rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[95dvh] md:max-h-[90vh] flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b hairline sticky top-0 bg-[hsl(var(--surface))] rounded-t-2xl z-10">
          <h3 className="font-display text-[19px] font-bold text-[hsl(var(--text-strong))]">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full grid place-items-center hover:bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))] transition-colors"
            aria-label={t("close")}
            data-testid="modal-close"
          >
            <X size={15} strokeWidth={2.2} />
          </button>
        </div>
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {children}
        </div>
      </div>
      <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}
